import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { useUnpinRealtyMutation } from '../../../../../redux/services/realties/realtiesApi';
import { useIsMapEdit } from '../../../../../contexts/MapEditContext';

const formatCoord = value => Number(value).toFixed(6);

/**
 * Realty's manual map pin, shown in AddRealtyForm only when the form was
 * opened from the map. `pendingPin` is the dropped pin of a realty being
 * created; otherwise the saved pin of the edited realty is shown.
 */
export const RealtyMapPinInfo = ({ realty = {}, pendingPin = null }) => {
  const isMapEdit = useIsMapEdit();
  const userToken = useSelector(selectUserToken);
  const [unpinRealty, { isLoading }] = useUnpinRealtyMutation();
  const [isCleared, setIsCleared] = useState(false);

  const location = realty?.location;
  const savedPin =
    location?.pinned_at && !isCleared
      ? { lat: location.latitude, lng: location.longitude }
      : null;
  const pin = pendingPin ?? savedPin;

  if (!isMapEdit || !pin) return null;

  const handleClear = async () => {
    try {
      await unpinRealty({ token: userToken, id: realty.id }).unwrap();
      setIsCleared(true);
      toast.success('Map pin cleared');
    } catch {
      toast.error('Could not clear map pin');
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 w-full px-3 py-2 text-xs bg-rose-50 border border-rose-200 rounded">
      <span className="flex items-center gap-1.5">
        <MapPin
          size={14}
          className="shrink-0 text-rose-600"
        />
        Map pin: {formatCoord(pin.lat)}, {formatCoord(pin.lng)}
        <span className="text-gray-500">
          {pendingPin ? '· from the map, saved with the realty' : '· set from map'}
        </span>
      </span>

      {!pendingPin && (
        <button
          type="button"
          onClick={handleClear}
          disabled={isLoading}
          className="shrink-0 underline text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          Clear pin
        </button>
      )}
    </div>
  );
};
